define((require) => {
    "use strict";

    const {range}                           = require("underscore");
    const $                                 = require("jquery");
    const CharacterPropertiesViewController = require("ui/experience/widgets/CharacterPropertiesViewController");
    const ColorPicker                       = require("shared/services/ColorPicker");
    const FontProvider                      = require("shared/data/fonts/FontProvider");
    const InputSelection                    = require("services/InputSelection");
    const template                          = require("ui/experience/widgets/CharacterPropertiesView.tpl");
    const Preconditions                     = require("shared/util/Preconditions");
    const Tooltip                           = require("ui/common/Tooltip");
    const WebFontsUtil                      = require("shared/ui/experience/fonts/WebFontsUtil");
    const WebFontVariants                   = require("shared/ui/experience/fonts/WebFontVariants").default;
    const AbstractWidgetPropertiesView      = require("ui/experience/widgets/AbstractWidgetPropertiesView");
    const TextEditorConstants               = require("ui/experience/widgets/textWidget/textEditor/TextEditorConstants");

    const MIN_LETTER_SPACING = -9;
    const MAX_LETTER_SPACING = 10;

    const TextCaseOptions = {
        UPPERCASE: "uppercase",
        SMALL_CAPS: "small-caps"
    };
    const TEXT_SHADOW_STYLE = "2px 2px 6px rgba(0,0,0,0.5)";

    const EXPAND_PROPERTY = "characterExpanded";

    /**
     * Properties view for character details, like fonts and colors.
     *
     * @class CharacterPropertiesView
     * @constructor
     *
     * @cfg {EditorModel} model The editor model that is nested in the text widget model.
     */
    const CharacterPropertiesView = AbstractWidgetPropertiesView.extend({

        expandProperty: EXPAND_PROPERTY,

        _colorPicker: null,

        widgetView: null,

        template,

        templateHelpers() {
            return {
                TextCaseOptions
            };
        },

        controller: CharacterPropertiesViewController,

        initialize() {
            this.__super();
            this.widgetView = this.options.widgetView;
        },

        ui: {
            container: ".character-properties-container",
            legend: "[data-target=\".character-properties-container\"]",
            fontSelector: ".font-selector",
            colorInput: ".color-input",
            colorBox: ".font-color-box",
            fontInfoExclamationSign: ".font-info-section span",
            fontSize: ".font-size",
            fontStyle: ".font-style",
            lineHeight: ".line-height",
            textAlign: ".text-align",
            textAlignmentSection: ".alignment-section",
            fontLetterSpacing: ".font-letter-spacing",
            textDecoration: ".text-decoration",
            textDecorationButtons: ".change-text-decoration",
            textDecorationSection: ".decoration-section",
            changeTextCaseButtons: ".change-text-case",
            uppercaseButton: ".text-transform-uppercase",
            smallCapsButton: ".font-variant-smallcaps",
            textShadow: ".text-shadow",
            textShadowButton: ".change-text-shadow",
            nodeTypeInfo: ".node-name-section .info-tooltip"
        },

        events: {
            "click @ui.colorBox": "_handleColorBoxClick",
            "click .change-text-align": "_handleChangeAlignClick",
            "click @ui.changeTextCaseButtons": "_handleChangeTextCaseClick",
            "click @ui.textDecorationButtons": "_handleChangeDecorationClick",
            "click @ui.textShadowButton": "_handleChangeTextShadowClick",
            "shown.bs.collapse .character-properties-container.collapse-target": "_handleExpand",
            "hidden.bs.collapse .character-properties-container.collapse-target": "_handleCollapse",
            "mousedown": "_handleMouseDown",
            "select2-focus": "_handleSelectFocus"
        },

        bindings: {
            ".color-input": {
                observe: "color",

                onSet(color) {
                    return this._colorPicker.hexOrDefault(color);
                }
            },

            ".font-size": {
                observe: "fontSize",
                onGet(value) {
                    if (value === null) {
                        return "";
                    }

                    return Math.round(value);
                },

                onSet(value) {
                    // prevent string, 0 and negative value
                    return Math.max(5, parseInt(value, 10) || 5);
                }
            },

            ".line-height": {
                observe: "lineHeight",
                onGet(value) {
                    return value || "";
                },

                onSet(value) {
                    // prevent string, 0 and negative value
                    return Math.max(0.5, parseFloat(value) || 1);
                }
            },

            ".font-letter-spacing": {
                observe: "letterSpacing",
                onGet(value) {
                    // Default value for letter spacing can be "normal", so we normalize it to zero.
                    if (value === "normal") {
                        return 0;
                    }

                    return parseInt(value, 10);
                },

                onSet(value) {
                    return parseInt(value, 10);
                }
            },

            ".text-align": {
                observe: "textAlign",
                onGet(value) {
                    return value || "none";
                },
                update($el, value) {
                    this._updateAlignment(value);
                }
            },

            ".font-selector": {
                observe: ["fontFamily", "fontProvider", "fontMetadata"],

                onSet() {
                    const data = this.getSelectedFontData();
                    const provider = data.fontDescriptor.get("provider").value;
                    const fontMetadata = data.fontDescriptor.get("data") || {};

                    return [data.fontFamily, provider, fontMetadata];
                },

                update($el, values) {
                    const fontFamily = values[0];
                    let fontDescription = null;

                    // When fontFamily is set to `null`, stickit converts it to
                    // an empty string for the events. See:
                    // https://github.com/NYTimes/backbone.stickit/blob/master/backbone.stickit.js#L279
                    if (fontFamily) {
                        fontDescription = {id: fontFamily};
                    }

                    this.ui.fontSelector.select2("val", fontDescription);
                }
            },

            ".font-style": {
                observe: ["fontStyle", "fontWeight", "fontFamily"],
                selectOptions: {
                    collection() {
                        const selectedData = this.getSelectedFontData();

                        if (selectedData) {
                            return ["Select style"].concat(WebFontsUtil.variantsFromFontDescriptor(selectedData.fontDescriptor));
                        }

                        // Don't show any font styles
                        return {};
                    },

                    labelPath: "label",
                    valuePath: "value"
                },

                onSet(value) {
                    const selectedData = this.getSelectedFontData();
                    const css = WebFontsUtil.toCss(selectedData.fontFamily, value, this.model.get("fontProvider"));

                    return [css.fontStyle, css.fontWeight, css.fontFamily];
                },

                onGet(values /* fontStyle, fontWeight, fontFamily */) {
                    if (values[2]) {
                        const providerValue = this.model.get("fontProvider");
                        const provider = FontProvider.fromValue(providerValue);
                        const variant = WebFontVariants.findVariant(provider, values[0], values[1]);
                        const variantValue = variant && variant.value;

                        return variantValue;
                    }
                }
            },

            ".text-decoration": {
                observe: "textDecoration",
                update($el, value) {
                    this._updateDecorationLine(value);
                }
            },

            ".text-transform-uppercase": {
                observe: "textTransform",
                update($el, value) {
                    const selected = (value === TextCaseOptions.UPPERCASE);

                    $el.toggleClass("selected", selected);
                }
            },

            ".font-variant-smallcaps": {
                observe: "fontVariant",
                update($el, value) {
                    const selected = (value === TextCaseOptions.SMALL_CAPS);

                    $el.toggleClass("selected", selected);
                }
            },

            ".text-shadow": {
                observe: "textShadow",

                update($el, value) {
                    this._updateTextShadow(value);
                }
            },

            ".node-name": {
                observe: "nodeName",

                selectOptions: {
                    collection() {
                        // Don't allow a widget level node name change if there are multiple paragraphs
                        const multipleParagraphs = this.widgetView.$el.children().length > 1;
                        const isEditable = this.widgetView.textEditorController.getViewEditable();

                        if (!isEditable && multipleParagraphs) {
                            return {};
                        }

                        const options = TextEditorConstants.HEADINGS.map((heading, i) => ({label: `Heading ${++i}`, value: heading}));
                        options.unshift({label: "Default", value: ""});
                        return options;
                    }
                },

                onSet(value) {
                    if (value === "") {
                        return null;
                    }

                    return value;
                }
            }
        },

        modelEvents: {
            "change:color": "_changeColorHandler"
        },

        _findValidBlockElement(el) {
            if (!el) {
                return;
            }

            if (el.nodeType === 3 || el.nodeName.toLowerCase() === TextEditorConstants.TAGS.SPAN) {
                return this._findValidBlockElement(el.parentNode);
            }

            return el;
        },

        _handleColorBoxClick() {
            this.ui.colorInput.colorpicker("show");
        },

        _handleChangeAlignClick(event) {
            // On Mac Safari and Chrome, an extra click is fired on the
            // left align button unless this is included
            event.preventDefault();
            this.model.set("textAlign", event.currentTarget.getAttribute("data-align"));
        },

        _handleChangeDecorationClick(event) {
            // On Mac Safari and Chrome, an extra click is fired on the
            // left align button unless this is included
            event.preventDefault();

            const selectedDecorationLine = event.currentTarget.getAttribute("data-value");
            let decorationLine = (this.model.get("textDecoration") || "").split(" ");

            const existingIndex = decorationLine.indexOf(selectedDecorationLine);

            if (existingIndex > -1) {
                decorationLine.splice(existingIndex, 1);
            } else if (decorationLine.indexOf("none") > -1) {
                decorationLine = [selectedDecorationLine];
            } else {
                decorationLine = decorationLine.concat([selectedDecorationLine]);
            }

            if (decorationLine.length === 0) {
                decorationLine.push("none");
            }

            this.model.set("textDecoration", decorationLine.join(" "));
        },

        _handleChangeTextShadowClick(event) {
            event.preventDefault();

            const textShadow = this.model.get("textShadow") === "none" ? TEXT_SHADOW_STYLE : "none";

            this.model.set("textShadow", textShadow);
        },

        // Select2 input is outside the character property view, mousedown on the font-family input
        // won't trigger this event, so we need to trigger this on select2.
        _handleSelectFocus() {
            this.model.trigger("focus:propertyView");

            // Must be done manually because select2 library stop
            // propagation of the event.
            this._closeInputSelectors();
        },

        // Not using click, because that will make the font-style drop list disappear automatically on first open
        _handleMouseDown() {
            this.model.trigger("focus:propertyView");
        },

        _handleExpand() {
            this.viewScope.viewModel.get("widgetPropertyViewsState")[this.expandProperty] = true;
        },

        _handleCollapse() {
            this.viewScope.viewModel.get("widgetPropertyViewsState")[this.expandProperty] = false;
        },

        _handleUppercase(isSelectedNow) {
            if (isSelectedNow) {
                if (this.ui.smallCapsButton.hasClass("selected")) {
                    this.model.set("fontVariant", "none");
                }

                this.model.set("textTransform", TextCaseOptions.UPPERCASE);
            } else {
                this.model.set("textTransform", "none");
            }
        },

        _handleSmallCaps(isSelectedNow) {
            if (isSelectedNow) {
                if (this.ui.uppercaseButton.hasClass("selected")) {
                    this.model.set("textTransform", "none");
                }

                this.model.set("fontVariant", TextCaseOptions.SMALL_CAPS);
            } else {
                this.model.set("fontVariant", "none");
            }
        },

        _handleChangeTextCaseClick(event) {
            const $target = $(event.currentTarget);
            const wasSelectedBefore = $target.hasClass("selected");
            const isSelectedNow = !wasSelectedBefore;

            // flip if the button is selected or not
            $target.toggleClass("selected", isSelectedNow);

            switch ($target.attr("data-option")) {
                case TextCaseOptions.UPPERCASE:
                    this._handleUppercase(isSelectedNow);
                    break;
                case TextCaseOptions.SMALL_CAPS:
                    this._handleSmallCaps(isSelectedNow);
                    break;
                default:
                    Preconditions.fail(`unhandled text case click change reached: ${$target.attr("data-option")}`);
                    break;
            }
        },

        _changeColorHandler(model) {
            this._updateColorBox(model.changed.color);
        },

        /**
         * Updates the color on the little colored box to the
         * right of the color input field.
         *
         * @param {String} color - The hex value of the color. Eg., "#ff0000".
         *                         Empty string or `null` is also acceptable
         *                         input.
         */
        _updateColorBox(color) {
            this.ui.colorBox
                .css("backgroundColor", color)
                .toggleClass("no-color", !color);
        },

        _updateAlignment(textAlign) {
            // Clear previous selections
            this.ui.textAlignmentSection.find("button").removeClass("selected");

            const $button = this.ui.textAlignmentSection.find(`button[data-align='${textAlign}']`);
            $button.addClass("selected");
        },

        _updateDecorationLine(textDecoration = "") {
            this.ui.textDecorationButtons.each((i, button) => {
                $(button).toggleClass("selected", textDecoration.indexOf(button.getAttribute("data-value")) > -1);
            });
        },

        _updateTextShadow(textShadow = "none") {
            const isShadowApplied = textShadow !== "none";

            this.ui.textShadowButton.toggleClass("selected", isShadowApplied);
        },

        onRender() {
            this.__super();
            this._colorPicker = new ColorPicker({
                color: this.model.get("color"),
                input: this.ui.colorInput,
                colorBox: this.ui.colorBox
            });

            this.ui.fontSelector.select2({
                placeholder: "Select a font",
                dropdownAutoWidth: true,
                initSelection: this.fontSelectorInitSelection,
                query: this.fontSelectorQuery,
                formatResult: this._fontSelectorFormatResult,
                dropdownCssClass: "font-selector-dropdown"
            });

            this.fontSizeInputSelection = new InputSelection({
                $input: this.ui.fontSize,
                choices: [8, 9, 10, 11, 12, 14, 18, 24, 30, 36, 48, 60, 72, 96]
            });

            // Subclasses might decide not to render the line height control.
            if (this.ui.lineHeight.length) {
                this.lineHeightInputSelection = new InputSelection({
                    $input: this.ui.lineHeight,
                    choices: [0.8, 1, 1.2, 1.4, 1.5, 2, 2.5]
                });
            }

            if (this.ui.fontLetterSpacing.length) {
                this.fontLetterSpacingInputSelection = new InputSelection({
                    $input: this.ui.fontLetterSpacing,
                    choices: range(MIN_LETTER_SPACING, MAX_LETTER_SPACING + 1)
                });
            }

            Tooltip.add(this.ui.fontInfoExclamationSign, {
                delay: {show: 500, hide: 0},
                placement: "right",
                width: 220
            });

            Tooltip.add(this.ui.nodeTypeInfo, {
                delay: {show: 500, hide: 0},
                placement: "right",
                width: 220
            });
        },

        // eslint-disable-next-line no-unused-vars
        fontSelectorInitSelection(element, callback) {},

        // eslint-disable-next-line no-unused-vars
        fontSelectorQuery(options) {},

        _fontSelectorFormatResult(object, container) {
            const className = object.children ? "header" : "option";

            container.addClass(className);

            if (object.hasOwnProperty("fontDescriptor")) {
                const fontDescriptor = object.fontDescriptor;
                const fontFamily = WebFontsUtil.quoteSpecialFontNames(fontDescriptor.get("cssFamily"));

                switch (fontDescriptor.get("provider")) {
                    case FontProvider.FONTS_DOT_COM:
                    case FontProvider.TYPEKIT:
                        return CharacterPropertiesView._markupForFontWithId(object, fontDescriptor, fontFamily);

                    default:
                        container.css({
                            fontFamily: fontFamily
                        });
                }
            }

            return object.text;
        },

        _closeInputSelectors() {
            this.fontSizeInputSelection.closeDropdown();
            if (this.fontLetterSpacingInputSelection) {
                this.fontLetterSpacingInputSelection.closeDropdown();
            }

            if (this.lineHeightInputSelection) {
                this.lineHeightInputSelection.closeDropdown();
            }
        },

        getSelectedFontData() {
            return this.ui.fontSelector.select2("data");
        },

        onClose() {
            this._colorPicker.dispose();
            this.fontSizeInputSelection.dispose();
            if (this.fontLetterSpacingInputSelection) {
                this.fontLetterSpacingInputSelection.dispose();
            }

            if (this.lineHeightInputSelection) {
                this.lineHeightInputSelection.dispose();
            }
        }
    }, {
        EXPAND_PROPERTY,

        _markupForFontWithId(object, fontDescriptor, fontFamily) {
            const fontProvider = fontDescriptor.get("provider");
            let fontProjectLabel;
            let html = `<div class="font-name" style="font-family: ${fontFamily}">${object.text}</div>`;

            switch (fontProvider) {
                case FontProvider.FONTS_DOT_COM:
                    fontProjectLabel = fontDescriptor.get("data").projectName;
                    html += `<div class="font-project-name">Project: ${fontProjectLabel}</div>`;
                    break;

                case FontProvider.TYPEKIT:
                    fontProjectLabel = fontDescriptor.get("data").kitID;
                    html += `<div class="font-project-name">Kit: ${fontProjectLabel}</div>`;
                    break;

                default:
                    // TODO Candidate for refactoring during MOS-2934.
                    Preconditions.warn(`DefaultSwitchWarn - Font Provider '${fontProvider}' not recognized.`);
                    break;
            }

            return $(`<div>${html}</div>`);
        }
    });

    return CharacterPropertiesView;
});



// WEBPACK FOOTER //
// ./src/editor/ui/experience/widgets/CharacterPropertiesView.js